import { useSession } from "hooks/useSession";
import { useState, useRef } from "react";
import { fetchWrapper } from "utils/apiFetch";


// ─── Config ──────────────────────────────────────────────────────────────────
const API_URL    = "/api/pan/MannualNewPanManager/";
const AADHAR_API = "https://pro.mokshiq.in/verify_aadhar/";
const AABHA_BASE = "https://aabha.diviniq.store";

// ─── Static data ─────────────────────────────────────────────────────────────
const INCOME_OPTIONS = [
  { value:"salary",         label:"Salary" },
  { value:"business",       label:"Business / Profession" },
  { value:"house_property", label:"House Property" },
  { value:"capital_gains",  label:"Capital Gains" },
  { value:"other_sources",  label:"Other Sources" },
  { value:"no_income",      label:"No Income" },
];
const POI_OPTS = ["AADHAAR Card issued by UIDAI","Passport","Voter ID Card","Driving License","Ration Card","Bank Certificate"];
const POA_OPTS = ["AADHAAR Card issued by UIDAI","Passport","Voter ID Card","Driving License","Electricity Bill","Bank Statement"];
const POD_OPTS = ["Central Govt Health Scheme Card","AADHAAR Card issued by UIDAI","Birth Certificate","Passport","Matriculation Certificate","Driving License"];

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = { navy:"#1a237e", gold:"#c8a84b", light:"#f5f7ff", border:"#dde1f0", text:"#1c1e2e", muted:"#6b7087", err:"#c0392b", ok:"#1a7e4a" };

// ─── Tiny reusable bits ───────────────────────────────────────────────────────
const css = (...obj) => Object.assign({}, ...obj);

const Inp = ({ value, onChange, placeholder, maxLength, readOnly, type="text", style={} }) => (
  <input
    type={type} value={value} placeholder={placeholder} maxLength={maxLength}
    readOnly={readOnly} onChange={onChange}
    style={css({
      height:36, padding:"0 10px", border:`1.5px solid ${C.border}`,
      borderRadius:6, fontSize:13, color:C.text, width:"100%",
      background:readOnly?"#f5f5f5":"#fff", fontFamily:"inherit",
      boxSizing:"border-box", outline:"none",
    }, style)}
  />
);

const Sel = ({ value, onChange, options }) => (
  <select value={value} onChange={onChange} style={{ height:36, padding:"0 10px", border:`1.5px solid ${C.border}`, borderRadius:6, fontSize:13, color:C.text, width:"100%", fontFamily:"inherit", outline:"none", background:"#fff" }}>
    {options.map(o => <option key={typeof o==="string"?o:o.value} value={typeof o==="string"?o:o.value}>{typeof o==="string"?o:o.label}</option>)}
  </select>
);

const Fld = ({ label, req, hint, children, style={} }) => (
  <div style={css({ display:"flex", flexDirection:"column", gap:3 }, style)}>
    <label style={{ fontSize:11, fontWeight:600, color:C.muted }}>
      {label}{req && <span style={{ color:C.err, marginLeft:2 }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize:10, color:C.muted }}>{hint}</span>}
  </div>
);

const RadioG = ({ value, onChange, options }) => (
  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:2 }}>
    {options.map(o => (
      <label key={o.value} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", border:`1.5px solid ${value===o.value?C.navy:C.border}`, borderRadius:20, cursor:"pointer", fontSize:12, background:value===o.value?C.light:"#fff", fontWeight:value===o.value?600:400, color:value===o.value?C.navy:C.text, userSelect:"none" }}>
        <input type="radio" checked={value===o.value} onChange={()=>onChange(o.value)} style={{ accentColor:C.navy, width:13, height:13 }} />{o.label}
      </label>
    ))}
  </div>
);

const CheckG = ({ value, onChange, options }) => (
  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:7, marginTop:4 }}>
    {options.map(o => {
      const on = value.includes(o.value);
      return (
        <label key={o.value} style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 10px", border:`1.5px solid ${on?C.navy:C.border}`, borderRadius:6, cursor:"pointer", fontSize:12, background:on?C.light:"#fff", fontWeight:on?600:400, color:on?C.navy:C.text }}>
          <input type="checkbox" checked={on} onChange={()=>onChange(on?value.filter(v=>v!==o.value):[...value,o.value])} style={{ accentColor:C.navy, width:13, height:13 }} />{o.label}
        </label>
      );
    })}
  </div>
);

const Sub = ({ n, label, children }) => (
  <div style={{ marginBottom:18 }}>
    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".8px", color:C.navy, marginBottom:9, paddingBottom:5, borderBottom:`2px solid ${C.gold}`, display:"flex", alignItems:"center", gap:6 }}>
      <span style={{ background:C.gold, color:C.navy, width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, flexShrink:0 }}>{n}</span>{label}
    </div>
    {children}
  </div>
);

const Card = ({ title, badge, children }) => (
  <div style={{ background:"#fff", borderRadius:8, border:`1px solid ${C.border}`, marginBottom:14 }}>
    <div style={{ background:C.navy, color:"#fff", padding:"11px 18px", borderRadius:"8px 8px 0 0", display:"flex", alignItems:"center", gap:10 }}>
      <span style={{ fontSize:13, fontWeight:700 }}>{title}</span>
      {badge && <span style={{ background:C.gold, color:C.navy, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, marginLeft:"auto" }}>{badge}</span>}
    </div>  
    <div style={{ padding:18 }}>{children}</div>
  </div>
);

const NavBtns = ({ onBack, onNext, backLabel="← Back", nextLabel, isSubmit=false, disabled=false }) => (
  <div style={{ display:"flex", justifyContent:"space-between", marginTop:20, paddingTop:14, borderTop:`1px solid ${C.border}` }}>
    <button onClick={onBack} style={{ background:"#fff", border:`1.5px solid ${C.border}`, color:C.muted, padding:"9px 22px", margin:"20px", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer" }}>{backLabel}</button>
    <button onClick={onNext} disabled={disabled} style={{ background:isSubmit?C.gold:C.navy, color:isSubmit?C.navy:"#fff", margin:"20px",padding:"9px 22px", borderRadius:6, fontSize:13, fontWeight:700, cursor:disabled?"not-allowed":"pointer", border:"none", opacity:disabled?.6:1 }}>{nextLabel}</button>
  </div>
);

const UpBox = ({ label, req, accept, icon, hint, file, onChange }) => {
  const ref = useRef();
  const prev = file?.type?.startsWith("image/") ? URL.createObjectURL(file) : null;
  return (
    <div style={{ marginBottom:0 }}>
      <div style={{ fontSize:10, fontWeight:600, color:C.muted, marginBottom:5 }}>{label}{req && <span style={{ color:C.err }}> *</span>}</div>
      <div onClick={()=>ref.current.click()} style={{ border:`2px dashed ${file?C.ok:C.border}`, borderRadius:8, padding:12, textAlign:"center", cursor:"pointer", background:file?"#e8f5ee":"#fafbff", minHeight:110, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
        <input ref={ref} type="file" accept={accept} onChange={e=>onChange(e.target.files[0]||null)} style={{ display:"none" }} />
        {prev ? <img src={prev} alt="" style={{ width:60, height:60, objectFit:"cover", borderRadius:4, marginBottom:4 }} />
               : <div style={{ fontSize:20, marginBottom:4 }}>{icon}</div>}
        <div style={{ fontSize:10, color:C.muted, lineHeight:1.3 }}>
          <strong style={{ display:"block", color:file?C.ok:C.navy, fontSize:10 }}>{file?"✓ "+file.name.slice(0,20):"Click to upload"}</strong>
          {!file && hint}
        </div>
      </div>
    </div>
  );
};

const OtpModal = ({ data, onConfirm, onCancel }) => {
  const [v,setV] = useState("");
  if (!data) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200 }}>
      <div style={{ background:"#fff", borderRadius:8, padding:28, width:340, maxWidth:"95vw", boxShadow:"0 8px 32px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:C.navy, marginBottom:6 }}>{data.title}</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>{data.desc}</div>
        <input autoFocus type="text" maxLength={6} value={v} onChange={e=>setV(e.target.value.replace(/\D/g,""))}
          onKeyDown={e=>e.key==="Enter"&&onConfirm(v)}
          placeholder="------"
          style={{ fontSize:22, textAlign:"center", letterSpacing:8, border:`2px solid ${C.border}`, borderRadius:8, width:"100%", height:52, marginBottom:14, outline:"none", boxSizing:"border-box" }} />
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>{setV("");onCancel();}} style={{ flex:1, padding:10, borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", background:"#f5f5f5", color:C.muted, border:"none" }}>Cancel</button>
          <button onClick={()=>{onConfirm(v);setV("");}} style={{ flex:1, padding:10, borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", background:C.navy, color:"#fff", border:"none" }}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

// ─── Default form state ───────────────────────────────────────────────────────
const DEFAULT_FORM = {
  first_name:"", middle_name:"", last_name:"", fullname:"", name_as_per_aadhaar:"",
  category:"Individual", existing_pan:"", gender:"", dob:"", aadharnum:"",
  is_minor:false, ftitle:"", father_first:"", father_middle:"", father_last:"",
  fathername:"", mother_first:"", mother_middle:"", mother_last:"",
  mother_name_full:"", parent_on_card:"Father", income_sources:[],
  poi_doc:POI_OPTS[0], poa_doc:POA_OPTS[0], pod_doc:POD_OPTS[0],
  res_flat:"", res_road:"", res_po:"", res_area:"", res_district:"",
  res_state:"", res_pin:"", res_country:"INDIA",
  aadhar_addess:"aadhar", shopname:"", saddess:"", sarea:"",
  svillage:"", sdistrict:"", sstate:"", spincode:"",
  comm_address:"Residence", ao_area_code:"", ao_type:"", ao_range_code:"", ao_no:"",
  place:"", date:new Date().toISOString().split("T")[0],
  mobile:"", email:"", pin:"", application_number:"",
};

// ─── Main export ──────────────────────────────────────────────────────────────
export default function PanApplicationForm() {
  const [appType, setAppType] = useState("");   // "new" | "change"
  const [step,    setStep]    = useState(1);
  const [form,    setForm]    = useState(DEFAULT_FORM);
  const [files,   setFiles]   = useState({ photo:null, signature:null, aadharfront:null, aadharback:null, doc1:null, doc2:null });
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("Processing…");
  const [alert,   setAlert]   = useState(null);   // {type:"ok"|"err", msg}
  const [result,  setResult]  = useState(null);
  const [otpData, setOtpData] = useState(null);   // {title,desc,resolve}
  const [aadharSt, setAadharSt] = useState(null); // {type,msg}
  const [aabhaSt,  setAabhaSt]  = useState(null);
  const [fetchingAadhar, setFetchingAadhar] = useState(false);
  const [birthCert, setBirthCert] = useState(null);

  const { userid } = useSession();

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const set   = (k,v) => setForm(f=>({...f,[k]:v}));
  const scroll = () => window.scrollTo({top:0,behavior:"smooth"});
  const goStep = n => { setStep(n); scroll(); };

  const updateName = (k,v) => {
    const u = {...form,[k]:v.toUpperCase()};
    u.fullname = [u.first_name,u.middle_name,u.last_name].filter(Boolean).join(" ");
    if (!form.name_as_per_aadhaar) u.name_as_per_aadhaar = u.fullname;
    setForm(u);
  };
  const updateFather = (k,v) => {
    const u = {...form,[k]:v.toUpperCase()};
    u.fathername = [u.father_first,u.father_middle,u.father_last].filter(Boolean).join(" ");
    setForm(u);
  };
  const updateMother = (k,v) => {
    const u = {...form,[k]:v.toUpperCase()};
    u.mother_name_full = [u.mother_first,u.mother_middle,u.mother_last].filter(Boolean).join(" ");
    setForm(u);
  };

  const askOtp = (title,desc) => new Promise(resolve => setOtpData({title,desc,resolve}));

  // ── Aadhaar auto-fill ─────────────────────────────────────────────────────────
  const fetchAadhaar = async () => {
    const aad = form.aadharnum.replace(/\s/g,"");
    if (aad.length!==12) { setAadharSt({type:"err",msg:"Enter valid 12-digit Aadhaar"}); return; }
    const pin = await askOtp("Transaction PIN","Enter your transaction PIN to continue");
    if (!pin||pin.length<4) return;
    setFetchingAadhar(true); setAadharSt({type:"info",msg:"⏳ Sending OTP to Aadhaar-linked mobile…"});
    try {
      const tok = localStorage.getItem("authToken")||"";
      const uid = localStorage.getItem("id")||"";
      const fd = new FormData();
fd.append("user_id", userid);
fd.append("aadhaar_number", aad);
fd.append("step", "send_otp");
fd.append("pin", pin);
fd.append("amount", "0");

const r1 = await fetchWrapper(AADHAR_API, 
 {
  method: "POST",
  body: fd
 }
);
      const j1  = r1;
      if (!j1.status) throw new Error(j1.message||"OTP send failed");
      const refId = j1.data?.ref_id?.toString()||"";
      setAadharSt({type:"info",msg:"📱 OTP sent — enter it below"});
      const otp = await askOtp("Aadhaar OTP","Enter OTP received on your Aadhaar-linked mobile");
      if (!otp) { setFetchingAadhar(false); return; }
      setAadharSt({type:"info",msg:"⏳ Verifying OTP…"});
      const fd1 = new FormData();
      fd1.append("user_id", userid);
      fd1.append("aadhaar_number", aad);
      fd1.append("step", "verify_otp");
      fd1.append("ref_id", refId);
      fd1.append("amount", "0");
      fd1.append("otp", otp);
      fd1.append("pin", pin);
      
      const r2 = await fetchWrapper(AADHAR_API, 
       {
        method: "POST",
        body: fd1
       }
      );
      // const r2 = await fetch(AADHAR_API,{method:"POST",headers:{"Content-Type":"application/json",...(tok?{"Authorization":"Token "+tok}:{})},body:JSON.stringify({user_id:uid,aadhaar_number:aad,step:"verify_otp",otp,ref_id:refId,pin,amount:"0"})});
      const j2 = r2;
      if (j2.new_data) { fillAadhaar(j2.new_data); setAadharSt({type:"ok",msg:"✅ Form auto-filled from Aadhaar!"}); }
      else throw new Error(j2.message||"Verification failed");
    } catch(e) { setAadharSt({type:"err",msg:"❌ "+e.message}); }
    setFetchingAadhar(false);
  };

  const fillAadhaar = d => {
    // Django verify_aadhar response structure:
    // { registered_name, name, dob:"YYYY-MM-DD" or "DD-MM-YYYY", gender:"M"/"F",
    //   aadhar:"12digits", care:"S/O Ramesh" or "D/O Ompal",
    //   zip/Zip:"110094", address:{ country, dist, state, po, subdist, street, house, landmark, loc, vtc } }
 
    // Name: prefer registered_name (Aadhaar exact), fallback to name
    const rawName = (d.registered_name || d.name || "").toUpperCase().trim();
    const parts   = rawName.split(/\s+/).filter(Boolean);
 
    const addr = d.address || {};
 
    // DOB: handle both YYYY-MM-DD and DD-MM-YYYY
    let dob = d.dob || "";
    if (/^\d{2}-\d{2}-\d{4}$/.test(dob)) {
      const [dd,mm,yy] = dob.split("-"); dob = `${yy}-${mm}-${dd}`;
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
      const [dd,mm,yy] = dob.split("/"); dob = `${yy}-${mm}-${dd}`;
    }
    // Already YYYY-MM-DD → keep as-is
 
    // Gender: "M" → "Male", "F" → "Female"
    const g = (d.gender || "").toUpperCase();
    const gender = g==="M"||g==="MALE" ? "Male" : g==="F"||g==="FEMALE" ? "Female" : "";
 
    // Father from care: "S/O Ramesh Kumar" → "RAMESH KUMAR"
    // "D/O Ompal" → "OMPAL"
    const care = (d.care || "")
      .replace(/^(S\/O|D\/O|W\/O|C\/O)\s*/i, "")
      .trim()
      .toUpperCase();
 
    // PIN
    const pin = String(d.zip || d.Zip || addr.pincode || "").replace(/\D/g,"");
 
    // Address fields — combine available sub-fields
    const flat   = [addr.house, addr.landmark].filter(Boolean).join(", ").toUpperCase();
    const road   = [addr.street, addr.subdist].filter(Boolean).join(", ").toUpperCase();
    const po     = (addr.po    || "").toUpperCase();
    const area   = (addr.loc   || addr.vtc   || "").toUpperCase();
    const dist   = (addr.dist  || d.dist     || "").toUpperCase();
    const state  = (addr.state || d.state    || "").toUpperCase();
    const country= (addr.country|| "INDIA").toUpperCase();
 
    // Aadhaar number — format with spaces
    const aadharFormatted = (d.aadhar || "")
      .replace(/\s/g,"")
      .replace(/(\d{4})(?=\d)/g,"$1 ")
      .trim();
 
    setForm(f => ({
      ...f,
      // Name
      first_name:          parts[0] || f.first_name,
      middle_name:         parts.length > 2 ? parts.slice(1,-1).join(" ") : "",
      last_name:           parts.length > 1 ? parts[parts.length-1] : f.last_name,
      fullname:            rawName || f.fullname,
      name_as_per_aadhaar: rawName || f.name_as_per_aadhaar,
      // Identity
      dob:                 dob    || f.dob,
      gender:              gender || f.gender,
      aadharnum:           aadharFormatted || f.aadharnum,
      // Address
      res_flat:     flat    || f.res_flat,
      res_road:     road    || f.res_road,
      res_po:       po      || f.res_po,
      res_area:     area    || f.res_area,
      res_district: dist    || f.res_district,
      res_state:    state   || f.res_state,
      res_pin:      pin     || f.res_pin,
      res_country:  country || f.res_country,
      // Declaration place
      place:        dist    || f.place,
      // Father from care field
      father_first: care    || f.father_first,
      fathername:   care    || f.fathername,
    }));
  };

  // ── Aadhaar birth cert ────────────────────────────────────────────────────────
  const fetchBirthCert = async () => {
    const aad = form.aadharnum.replace(/\s/g,"");
    const mob = form.mobile.replace(/\D/g,"").slice(-10);
    if (aad.length!==12) { setAabhaSt({type:"err",msg:"Enter Aadhaar number first"}); return; }
    if (mob.length<10)   { setAabhaSt({type:"err",msg:"Enter mobile number first"}); return; }
    setLoadMsg("Fetching birth certificate…"); setLoading(true);
    try {
      setAabhaSt({type:"info",msg:"⏳ Step 1/4 — sending Aadhaar OTP…"});
      const r1=await fetch(`${AABHA_BASE}/aadhaar-otp-send`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({aadhaar:aad})});
      const j1=await r1.json(); if(!j1.status) throw new Error(j1.message||"OTP send failed");
      const sid=j1.sessionId; setLoading(false);
      const o1=await askOtp("Aadhaar OTP","Enter OTP on your Aadhaar-linked mobile"); if(!o1) return;
      setLoading(true); setLoadMsg("Verifying Aadhaar OTP…");
      const r2=await fetch(`${AABHA_BASE}/aadhaar-otp-verify`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({otp:o1,sessionId:sid,mobile:mob})});
      const j2=await r2.json(); if(!j2.status) throw new Error(j2.message||"Aadhaar OTP failed");
      setLoading(false);
      const o2=await askOtp("Mobile OTP","Enter OTP sent to your mobile: "+mob); if(!o2) return;
      setLoading(true); setLoadMsg("Generating birth card…");
      const r3=await fetch(`${AABHA_BASE}/mobile-otp-verify`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:sid,otp:o2})});
      const j3=await r3.json(); if(!j3.status) throw new Error(j3.message||"Mobile OTP failed");
      const r4=await fetch(`${AABHA_BASE}/send-otp`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({mobile:mob})});
      const j4=await r4.json(); if(!j4.status) throw new Error(j4.message||"Download OTP failed");
      setLoading(false);
      const o3=await askOtp("Download OTP","Enter OTP to download birth certificate"); if(!o3) return;
      setLoading(true); setLoadMsg("Downloading PDF…");
      const r5=await fetch(`${AABHA_BASE}/verify-otp`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:j4.sessionId,otp:o3})});
      if(!r5.ok) throw new Error("Download failed: "+r5.status);
      const blob=await r5.blob();
      setBirthCert(new File([blob],"aadhaar_birth_cert.pdf",{type:"application/pdf"}));
      setAabhaSt({type:"ok",msg:"✅ Birth certificate ready — will be included in your bundle"});
    } catch(e) { setAabhaSt({type:"err",msg:"❌ "+e.message}); }
    setLoading(false);
  };

  // ── Validate ──────────────────────────────────────────────────────────────────
  const validate = () => {
    const e=[];
    if(!form.first_name)  e.push("First Name");
    if(!form.last_name)   e.push("Last Name");
    if(!form.gender)      e.push("Gender");
    if(!form.dob)         e.push("Date of Birth");
    if(form.aadharnum.replace(/\s/g,"").length!==12) e.push("Aadhaar (12 digits)");
    if(!form.father_first)e.push("Father First Name");
    if(!form.res_flat)    e.push("Address");
    if(!form.res_district)e.push("District");
    if(!form.res_state)   e.push("State");
    if(form.res_pin.length!==6) e.push("PIN Code");
    if(!form.place)       e.push("Place");
    if(!form.date)        e.push("Date");
    if(form.mobile.replace(/\D/g,"").length<10) e.push("Mobile");
    if(!form.email.includes("@")) e.push("Email");
    if(!form.pin)         e.push("Transaction PIN");
    if(!form.income_sources.length) e.push("Income Source");
    if(!files.photo)      e.push("Photo");
    if(!files.signature)  e.push("Signature");
    if(!files.aadharfront)e.push("Aadhaar Front");
    if(!files.aadharback) e.push("Aadhaar Back");
    if(appType==="change"&&!form.existing_pan) e.push("Existing PAN Number");
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const submit = async () => {
    const errs = validate();
    if(errs.length){ setAlert({type:"err",msg:"Please fill: "+errs.join(", ")}); goStep(1); return; }
    let mob = form.mobile.replace(/\D/g,"");
    if(!mob.startsWith("91")) mob="91"+mob;
    const fd = new FormData();
    fd.append("action","New Application");
    fd.append("application_type",appType);
    const skip = new Set(["income_sources","is_minor","aadhar_addess"]);
    Object.entries({...form,mobile:mob,aadharnum:form.aadharnum.replace(/\s/g,""),
      first_name:form.first_name.toUpperCase(), last_name:form.last_name.toUpperCase(),
      fullname:form.fullname.toUpperCase(), name_as_per_aadhaar:form.name_as_per_aadhaar.toUpperCase(),
      father_first:form.father_first.toUpperCase(), fathername:form.fathername.toUpperCase(),
    }).forEach(([k,v])=>{ if(!skip.has(k)) fd.append(k,v||""); });
    form.income_sources.forEach(v=>fd.append("income_sources",v));
    fd.append("is_minor",form.is_minor?"true":"false");
    fd.append("aadhar_addess",form.aadhar_addess);
    Object.entries(files).forEach(([k,f])=>{ if(f) fd.append(k,f); });
    if(birthCert) fd.append("birth_certificate",birthCert);
    setLoadMsg("Submitting & generating PDFs…"); setLoading(true); setAlert(null);
    try {
      const tok=localStorage.getItem("auth_token")||"";
      const r=await fetch(API_URL,{method:"POST",headers:tok?{"Authorization":"Token "+tok}:{},body:fd});
      const j=await r.json();
      setLoading(false);
      if(j.status){ setResult(j); setAlert({type:"ok",msg:`✅ Submitted! Reference: ${j.reference_num}`}); }
      else setAlert({type:"err",msg:"❌ "+(j.data||"Submission failed")});
    } catch(e){ setLoading(false); setAlert({type:"err",msg:"❌ Network error: "+e.message}); }
  };

  // ─── Type selector ────────────────────────────────────────────────────────────
  if(!appType) return (
    <div style={{ minHeight:"100vh", background:"#eef0f8", fontFamily:"'Segoe UI',system-ui,sans-serif", fontSize:14 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ background:C.navy, color:"#fff", padding:"14px 24px", marginTop:"40px",   display:"flex", alignItems:"center", borderBottom:`3px solid ${C.gold}` }}>
        <div><div style={{ fontSize:17, fontWeight:700 }}>🪪 PAN Card Application</div><div style={{ fontSize:11, opacity:.7 }}>Shree Ram Enterprises · Agent 87520</div></div>
        <span style={{ marginLeft:"auto", background:C.gold, color:C.navy, fontWeight:700, fontSize:11, padding:"4px 12px", borderRadius:20 }}>Select Type</span>
      </div>
      <div style={{ maxWidth:980, margin:"24px auto", padding:"0 12px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"#fff8e8", border:`1px solid ${C.gold}`, borderRadius:8, fontSize:12, marginBottom:20 }}>
          👆 <strong>Select application type to begin</strong>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          {[
            {t:"new",   ico:"🆕", title:"New PAN Card",              desc:"Apply for a fresh Permanent Account Number for the first time", badge:"Form 93 + Aadhaar Birth Proof"},
            {t:"change",ico:"✏️", title:"Change / Correction in PAN",desc:"Update name, DOB, address or other details in existing PAN",   badge:"Form CR"},
          ].map(({t,ico,title,desc,badge})=>(
            <div key={t} onClick={()=>{ setAppType(t); setStep(1); scroll(); }} style={{ border:`2px solid ${C.border}`, borderRadius:8, padding:24, cursor:"pointer", textAlign:"center", background:"#fff", transition:"all .2s" }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=C.navy}
              onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
              <div style={{ fontSize:34, marginBottom:8 }}>{ico}</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.navy, marginBottom:4 }}>{title}</div>
              <div style={{ fontSize:11, color:C.muted, lineHeight:1.4, marginBottom:8 }}>{desc}</div>
              <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:12, fontSize:10, fontWeight:700, background:"#e8f5ee", color:C.ok }}>{badge}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Main form ────────────────────────────────────────────────────────────────
  const STEPS = ["Personal","Parents","Address","Contact","Income & Docs","Files","Review"];
  const g2 = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 };
  const g3 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 };
  const g4 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 };
  const sp2 = { gridColumn:"span 2" };
  const sp3 = { gridColumn:"span 3" };

  return (
    <div style={{ minHeight:"100vh", background:"#eef0f8", fontFamily:"'Segoe UI',system-ui,sans-serif", fontSize:14, color:C.text }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input:focus,select:focus{border-color:${C.navy}!important;outline:none!important}`}</style>

      {/* Header */}
      <div style={{ background:C.navy, color:"#fff", padding:"14px 24px",marginTop:"40px", display:"flex", alignItems:"center", gap:14, borderBottom:`3px solid ${C.gold}`, position:"sticky", top:0, zIndex:100 }}>
        <div><div style={{ fontSize:17, fontWeight:700 }}>🪪 PAN Card Application</div><div style={{ fontSize:11, opacity:.7 }}>Shree Ram Enterprises · Agent 87520</div></div>
        <span style={{ marginLeft:"auto", background:C.gold, color:C.navy, fontWeight:700, fontSize:11, padding:"4px 12px", borderRadius:20 }}>{appType==="new"?"🆕 New PAN":"✏️ Change PAN"}</span>
      </div>

      {/* OTP Modal */}
      <OtpModal data={otpData} onConfirm={v=>{otpData?.resolve(v);setOtpData(null);}} onCancel={()=>{otpData?.resolve(null);setOtpData(null);}} />

      {/* Loader */}
      {loading && (
        <div style={{ position:"fixed", inset:0, background:"rgba(26,35,126,.75)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, flexDirection:"column", gap:16 }}>
          <div style={{ width:50, height:50, border:"4px solid rgba(255,255,255,.3)", borderTop:"4px solid #fff", borderRadius:"50%", animation:"spin .8s linear infinite" }} />
          <div style={{ color:"#fff", fontWeight:600, fontSize:14 }}>{loadMsg}</div>
        </div>
      )}

      <div style={{ maxWidth:980, margin:"20px auto", padding:"0 12px 60px" }}>

        {/* Alert */}
        {alert && (
          <div style={{ padding:"11px 14px", borderRadius:8, marginBottom:14, fontSize:12, lineHeight:1.5, background:alert.type==="err"?"#fdecea":"#e8f5ee", border:`1px solid ${alert.type==="err"?"#f5c6c6":"#b2dfdb"}`, color:alert.type==="err"?C.err:C.ok }}>
            {alert.msg}
          </div>
        )}

        {/* Steps nav */}
        <div style={{ display:"flex", borderRadius:8, overflow:"hidden", border:`1px solid ${C.border}`, marginBottom:6 }}>
          {STEPS.map((lbl,i)=>{
            const n=i+1, active=step===n, done=n<step;
            return (
              <button key={n} onClick={()=>goStep(n)} style={{ flex:1, padding:"8px 2px", textAlign:"center", background:active?C.navy:done?"#e8f5ee":"#fff", border:"none", cursor:"pointer", fontSize:10, fontWeight:600, color:active?"#fff":done?C.ok:C.muted, borderRight:i<STEPS.length-1?`1px solid ${C.border}`:"none", lineHeight:1.3 }}>
                <span style={{ display:"block", fontSize:16, fontWeight:700, lineHeight:1.1, marginBottom:2 }}>{n}</span>{lbl}
              </button>
            );
          })}
        </div>
        <div style={{ height:3, background:C.border, borderRadius:2, overflow:"hidden", marginBottom:18 }}>
          <div style={{ height:"100%", background:C.gold, borderRadius:2, width:`${(step/7)*100}%`, transition:"width .3s" }} />
        </div>

        {/* ══ STEP 1 Personal ══ */}
        {step===1 && (
          <Card title="Personal Information" badge="Part A · Fields 1–4">
            {/* Aadhaar auto-fill */}
            <div style={{ background:C.light, border:`1.5px solid ${C.border}`, borderRadius:8, padding:16, marginBottom:18 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.navy, marginBottom:10 }}>⚡ Auto-fill from Aadhaar</div>
              <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
                <Fld label="Aadhaar Number" req style={{ flex:1, minWidth:160 }}>
                  <Inp value={form.aadharnum} maxLength={14} placeholder="XXXX XXXX XXXX"
                    onChange={e=>set("aadharnum",e.target.value.replace(/\D/g,"").replace(/(\d{4})(?=\d)/g,"$1 ").trim())} />
                </Fld>
                <button disabled={fetchingAadhar} onClick={fetchAadhaar}
                  style={{ height:36, padding:"0 16px", background:fetchingAadhar?"#9e9e9e":C.navy, color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:fetchingAadhar?"not-allowed":"pointer" }}>
                  {fetchingAadhar?"Fetching…":"Fetch & Auto-fill"}
                </button>
              </div>
              {aadharSt && <div style={{ fontSize:11, marginTop:8, padding:"8px 12px", borderRadius:6, background:aadharSt.type==="ok"?"#e8f5ee":aadharSt.type==="err"?"#fdecea":"#e3f2fd", color:aadharSt.type==="ok"?C.ok:aadharSt.type==="err"?C.err:"#1565c0" }}>{aadharSt.msg}</div>}
            </div>

            <Sub n="1" label="Applicant Name">
              <div style={g3}>
                <Fld label="First Name" req><Inp value={form.first_name} placeholder="KUMKUM" onChange={e=>updateName("first_name",e.target.value)} /></Fld>
                <Fld label="Middle Name"><Inp value={form.middle_name} placeholder="(if any)" onChange={e=>updateName("middle_name",e.target.value)} /></Fld>
                <Fld label="Last Name" req><Inp value={form.last_name} placeholder="MAURYA" onChange={e=>updateName("last_name",e.target.value)} /></Fld>
                <Fld label="Full Name (auto)" style={sp3}><Inp value={form.fullname} readOnly /></Fld>
                <Fld label="Name as per Aadhaar" req hint="Printed on PAN card" style={sp3}><Inp value={form.name_as_per_aadhaar} onChange={e=>set("name_as_per_aadhaar",e.target.value.toUpperCase())} /></Fld>
              </div>
            </Sub>

            {appType==="change" && (
              <Sub n="★" label="Existing PAN Details">
                <div style={g2}>
                  <Fld label="Existing PAN Number" req><Inp value={form.existing_pan} maxLength={10} placeholder="ABCDE1234F" onChange={e=>set("existing_pan",e.target.value.toUpperCase())} /></Fld>
                  <Fld label="Category"><Sel value={form.category} onChange={e=>set("category",e.target.value)} options={["Individual","HUF","Company","Firm","Trust"]} /></Fld>
                </div>
              </Sub>
            )}

            <Sub n="2" label="Gender & Date of Birth">
              <div style={g2}>
                <Fld label="Gender" req><RadioG value={form.gender} onChange={v=>set("gender",v)} options={[{value:"Male",label:"Male"},{value:"Female",label:"Female"},{value:"Transgender",label:"Transgender"}]} /></Fld>
                <Fld label="Date of Birth" req><Inp type="date" value={form.dob} onChange={e=>set("dob",e.target.value)} /></Fld>
              </div>
            </Sub>

            <Sub n="12" label="Minor Applicant?">
              <Fld hint="Minor: auto-fills Part E (Representative Assessee) with father's details">
                <RadioG value={form.is_minor?"true":"false"} onChange={v=>set("is_minor",v==="true")} options={[{value:"false",label:"Adult (18+)"},{value:"true",label:"Minor (below 18)"}]} />
              </Fld>
            </Sub>

            <Fld label="Application Number" hint="Leave blank to auto-assign"><Inp value={form.application_number} onChange={e=>set("application_number",e.target.value)} /></Fld>

            <NavBtns onBack={()=>{setAppType("");scroll();}} backLabel="← Change Type" onNext={()=>goStep(2)} nextLabel="Next: Parents →" />
          </Card>
        )}

        {/* ══ STEP 2 Parents ══ */}
        {step===2 && (
          <Card title="Parent Details" badge="Part C · Fields 13–15">
            <Sub n="13" label="Father's Name">
              <div style={g3}>
                <Fld label="Title"><Sel value={form.ftitle} onChange={e=>set("ftitle",e.target.value)} options={[{value:"",label:"— Select —"},{value:"Shri",label:"Shri"},{value:"Late Shri",label:"Late Shri"}]} /></Fld>
                <Fld label="First Name" req><Inp value={form.father_first} onChange={e=>updateFather("father_first",e.target.value)} /></Fld>
                <Fld label="Middle Name"><Inp value={form.father_middle} onChange={e=>updateFather("father_middle",e.target.value)} /></Fld>
                <Fld label="Last Name" style={{gridColumn:"2/4"}}><Inp value={form.father_last} onChange={e=>updateFather("father_last",e.target.value)} /></Fld>
                <Fld label="Full Father Name (auto)" style={sp3}><Inp value={form.fathername} readOnly /></Fld>
              </div>
            </Sub>
            <Sub n="14" label="Mother's Name">
              <div style={g3}>
                <Fld label="First Name"><Inp value={form.mother_first} onChange={e=>updateMother("mother_first",e.target.value)} /></Fld>
                <Fld label="Middle Name"><Inp value={form.mother_middle} onChange={e=>updateMother("mother_middle",e.target.value)} /></Fld>
                <Fld label="Last Name"><Inp value={form.mother_last} onChange={e=>updateMother("mother_last",e.target.value)} /></Fld>
                <Fld label="Full Mother Name (auto)" style={sp3}><Inp value={form.mother_name_full} readOnly /></Fld>
              </div>
            </Sub>
            <Sub n="15" label="Parent Name to Print on PAN Card">
              <RadioG value={form.parent_on_card} onChange={v=>set("parent_on_card",v)} options={[{value:"Father",label:"Father's Name"},{value:"Mother",label:"Mother's Name"}]} />
            </Sub>
            <NavBtns onBack={()=>goStep(1)} onNext={()=>goStep(3)} nextLabel="Next: Address →" />
          </Card>
        )}

        {/* ══ STEP 3 Address ══ */}
        {step===3 && (
          <Card title="Address & AO Code" badge="Fields 5, 7, 16">
            <Sub n="5" label="Residence Address">
              <div style={g2}>
                <Fld label="Flat / Door / Building" req style={sp2}><Inp value={form.res_flat} onChange={e=>set("res_flat",e.target.value)} /></Fld>
                <Fld label="Road / Street / Block" style={sp2}><Inp value={form.res_road} onChange={e=>set("res_road",e.target.value)} /></Fld>
                <Fld label="Post Office"><Inp value={form.res_po} onChange={e=>set("res_po",e.target.value)} /></Fld>
                <Fld label="Area / Locality"><Inp value={form.res_area} onChange={e=>set("res_area",e.target.value)} /></Fld>
                <Fld label="District" req><Inp value={form.res_district} onChange={e=>set("res_district",e.target.value)} /></Fld>
                <Fld label="State" req><Inp value={form.res_state} onChange={e=>set("res_state",e.target.value)} /></Fld>
                <Fld label="PIN Code" req><Inp value={form.res_pin} maxLength={6} onChange={e=>set("res_pin",e.target.value.replace(/\D/g,""))} /></Fld>
                <Fld label="Country"><Inp value={form.res_country} onChange={e=>set("res_country",e.target.value.toUpperCase())} /></Fld>
              </div>
            </Sub>
            <Sub n="★" label="Address Type for Form">
              <RadioG value={form.aadhar_addess} onChange={v=>set("aadhar_addess",v)} options={[{value:"aadhar",label:"Aadhaar Address (₹112)"},{value:"shop",label:"Shop / Office Address (₹140)"}]} />
              {form.aadhar_addess==="shop" && (
                <div style={{...g3, marginTop:12}}>
                  <Fld label="Shop Name"><Inp value={form.shopname} onChange={e=>set("shopname",e.target.value)} /></Fld>
                  <Fld label="Shop Address"><Inp value={form.saddess} onChange={e=>set("saddess",e.target.value)} /></Fld>
                  <Fld label="Area"><Inp value={form.sarea} onChange={e=>set("sarea",e.target.value)} /></Fld>
                  <Fld label="Village / Town"><Inp value={form.svillage} onChange={e=>set("svillage",e.target.value)} /></Fld>
                  <Fld label="District"><Inp value={form.sdistrict} onChange={e=>set("sdistrict",e.target.value)} /></Fld>
                  <Fld label="State"><Inp value={form.sstate} onChange={e=>set("sstate",e.target.value)} /></Fld>
                  <Fld label="PIN"><Inp value={form.spincode} maxLength={6} onChange={e=>set("spincode",e.target.value.replace(/\D/g,""))} /></Fld>
                </div>
              )}
            </Sub>
            <Sub n="7" label="Communication Address">
              <RadioG value={form.comm_address} onChange={v=>set("comm_address",v)} options={[{value:"Residence",label:"Residence"},{value:"Office",label:"Office"}]} />
            </Sub>
            <Sub n="16" label="AO Code">
              <div style={g4}>
                <Fld label="Area Code"><Inp value={form.ao_area_code} placeholder="DEL" onChange={e=>set("ao_area_code",e.target.value.toUpperCase())} /></Fld>
                <Fld label="AO Type"><Inp value={form.ao_type} placeholder="W" onChange={e=>set("ao_type",e.target.value)} /></Fld>
                <Fld label="Range Code"><Inp value={form.ao_range_code} placeholder="123" onChange={e=>set("ao_range_code",e.target.value)} /></Fld>
                <Fld label="AO Number"><Inp value={form.ao_no} placeholder="12" onChange={e=>set("ao_no",e.target.value)} /></Fld>
              </div>
            </Sub>
            <Sub n="✍" label="Declaration">
              <div style={g2}>
                <Fld label="Place" req><Inp value={form.place} onChange={e=>set("place",e.target.value.toUpperCase())} /></Fld>
                <Fld label="Date" req><Inp type="date" value={form.date} onChange={e=>set("date",e.target.value)} /></Fld>
              </div>
            </Sub>
            <NavBtns onBack={()=>goStep(2)} onNext={()=>goStep(4)} nextLabel="Next: Contact →" />
          </Card>
        )}

        {/* ══ STEP 4 Contact ══ */}
        {step===4 && (
          <Card title="Contact Details" badge="Field 10">
            <Sub n="10" label="Mobile & Email">
              <div style={g2}>
                <Fld label="Mobile Number" req hint="10 digits — country code 91 auto-added">
                  <Inp value={form.mobile} maxLength={12} placeholder="9876543210" onChange={e=>set("mobile",e.target.value.replace(/\D/g,""))} />
                </Fld>
                <Fld label="Email ID" req>
                  <Inp type="email" value={form.email} placeholder="you@example.com" onChange={e=>set("email",e.target.value)} />
                </Fld>
              </div>
            </Sub>
            <Sub n="🔒" label="Transaction PIN">
              <div style={g2}>
                <Fld label="Transaction PIN" req hint="Required to authorise wallet deduction">
                  <Inp type="password" value={form.pin} maxLength={6} placeholder="4–6 digit PIN" onChange={e=>set("pin",e.target.value.replace(/\D/g,""))} />
                </Fld>
              </div>
            </Sub>
            <NavBtns onBack={()=>goStep(3)} onNext={()=>goStep(5)} nextLabel="Next: Income & Docs →" />
          </Card>
        )}

        {/* ══ STEP 5 Income & Docs ══ */}
        {step===5 && (
          <Card title="Income & Documents" badge="Part B & G">
            <Sub n="11" label="Source of Income">
              <CheckG value={form.income_sources} onChange={v=>set("income_sources",v)} options={INCOME_OPTIONS} />
            </Sub>
            <Sub n="23" label="Proof Documents">
              <div style={g3}>
                <Fld label="Proof of Identity" req><Sel value={form.poi_doc} onChange={e=>set("poi_doc",e.target.value)} options={POI_OPTS} /></Fld>
                <Fld label="Proof of Address" req><Sel value={form.poa_doc} onChange={e=>set("poa_doc",e.target.value)} options={POA_OPTS} /></Fld>
                <Fld label="Proof of Date of Birth" req><Sel value={form.pod_doc} onChange={e=>set("pod_doc",e.target.value)} options={POD_OPTS} /></Fld>
              </div>
            </Sub>
            <NavBtns onBack={()=>goStep(4)} onNext={()=>goStep(6)} nextLabel="Next: Files →" />
          </Card>
        )}

        {/* ══ STEP 6 Files ══ */}
        {step===6 && (
          <Card title="Photos & Documents" badge="Upload">
            {appType==="new" && (
              <Sub n="🏥" label="Aadhaar Birth Certificate (New PAN)">
                <div style={{ padding:12, background:"#e8f5ee", border:"1px solid #b2dfdb", borderRadius:8, marginBottom:10, fontSize:12 }}>
                  ✅ Auto-generated via Aadhaar API — replaces Form CR in your bundle.
                </div>
                <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
                  <Fld label="Mobile for download OTP" style={{ flex:1 }}>
                    <Inp value={form.mobile.slice(-10)} maxLength={10} placeholder="10-digit mobile" onChange={e=>set("mobile",e.target.value.replace(/\D/g,""))} />
                  </Fld>
                  <button disabled={!!birthCert} onClick={fetchBirthCert}
                    style={{ height:36, padding:"0 16px", background:birthCert?"#9e9e9e":C.navy, color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:birthCert?"not-allowed":"pointer" }}>
                    {birthCert?"✅ Certificate Ready":"Get Birth Certificate"}
                  </button>
                </div>
                {aabhaSt && <div style={{ fontSize:11, marginTop:8, padding:"8px 12px", borderRadius:6, background:aabhaSt.type==="ok"?"#e8f5ee":aabhaSt.type==="err"?"#fdecea":"#e3f2fd", color:aabhaSt.type==="ok"?C.ok:aabhaSt.type==="err"?C.err:"#1565c0" }}>{aabhaSt.msg}</div>}
              </Sub>
            )}
            <Sub n="📎" label="Required Files">
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:12 }}>
                <UpBox label="Photo" req icon="👤" accept="image/*" hint="4.5×3.5 cm, white bg" file={files.photo} onChange={f=>setFiles(p=>({...p,photo:f}))} />
                <UpBox label="Signature" req icon="✒️" accept="image/*" hint="Clear, white bg" file={files.signature} onChange={f=>setFiles(p=>({...p,signature:f}))} />
                <UpBox label="Aadhaar Front" req icon="📄" accept="image/*,application/pdf" hint="Front side" file={files.aadharfront} onChange={f=>setFiles(p=>({...p,aadharfront:f}))} />
                <UpBox label="Aadhaar Back" req icon="📄" accept="image/*,application/pdf" hint="Back side" file={files.aadharback} onChange={f=>setFiles(p=>({...p,aadharback:f}))} />
                <UpBox label="Supporting Doc 1" icon="📋" accept="image/*,application/pdf" hint="POI / POA / PODoB" file={files.doc1} onChange={f=>setFiles(p=>({...p,doc1:f}))} />
                <UpBox label="Supporting Doc 2" icon="📋" accept="image/*,application/pdf" hint="Additional doc" file={files.doc2} onChange={f=>setFiles(p=>({...p,doc2:f}))} />
              </div>
            </Sub>
            <NavBtns onBack={()=>goStep(5)} onNext={()=>goStep(7)} nextLabel="Review & Submit →" />
          </Card>
        )}

        {/* ══ STEP 7 Review ══ */}
        {step===7 && (
          <Card title="Review & Submit" badge={appType==="new"?"New PAN — Form 93":"Change PAN — Form CR"}>
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"#fff8e8", border:`1px solid ${C.gold}`, borderRadius:8, fontSize:12, marginBottom:16 }}>
              {appType==="new"
                ? <><strong>🆕 New PAN</strong> — Form 93 + Aadhaar Birth Certificate bundle</>
                : <><strong>✏️ Change PAN</strong> — Form CR bundle</>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9 }}>
              {[
                ["Application Type", appType==="new"?"New PAN":"Change PAN"],
                ["Full Name",        form.fullname],
                ["Aadhaar Name",     form.name_as_per_aadhaar],
                ["Gender",           form.gender],
                ["Date of Birth",    form.dob],
                ["Aadhaar No.",      form.aadharnum],
                ...(appType==="change"?[["Existing PAN",form.existing_pan]]:[]),
                ["Mobile",           form.mobile],
                ["Email",            form.email],
                ["Father Name",      form.fathername],
                ["Mother Name",      form.mother_name_full],
                ["District",         form.res_district],
                ["State",            form.res_state],
                ["PIN",              form.res_pin],
                ["Income",           form.income_sources.join(", ")||"—"],
                ["POI",              form.poi_doc],
                ["POA",              form.poa_doc],
                ["Place",            form.place],
                ["Minor",            form.is_minor?"Yes":"No"],
                ["Photo",            files.photo?.name||"—"],
                ["Signature",        files.signature?.name||"—"],
                ...(appType==="new"?[["Birth Cert",birthCert?"✅ Ready":"Not fetched"]]:[]),
              ].map(([l,v])=>(
                <div key={l} style={{ padding:"9px 12px", background:C.light, borderRadius:8 }}>
                  <div style={{ fontSize:10, color:C.muted, marginBottom:2 }}>{l}</div>
                  <div style={{ fontSize:12, fontWeight:600, wordBreak:"break-word" }}>{v||"—"}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, padding:12, background:"#fff8e8", border:`1.5px solid ${C.gold}`, borderRadius:8, fontSize:12 }}>
              ⚠️ <strong>Verify all details before submitting.</strong> Wallet will be debited and PDFs generated.
            </div>
            <NavBtns onBack={()=>goStep(6)} onNext={submit} nextLabel="⬇ Submit & Generate PDFs" isSubmit disabled={loading} />

            {result && (
              <div style={{ marginTop:18, padding:18, background:"#e8f5ee", border:"1px solid #b2dfdb", borderRadius:8 }}>
                <div style={{ fontWeight:700, color:C.ok, marginBottom:10, fontSize:15 }}>✅ Application Submitted!</div>
                <div style={{ fontSize:12, marginBottom:8 }}>Reference: <strong>{result.reference_num}</strong></div>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  {result.pdf_form93 && <a href={result.pdf_form93} target="_blank" rel="noreferrer" style={{ padding:"9px 16px", background:C.navy, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:12, fontWeight:700 }}>📄 Form 93</a>}
                  {result.pdf_formcr && <a href={result.pdf_formcr} target="_blank" rel="noreferrer" style={{ padding:"9px 16px", background:C.navy, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:12, fontWeight:700 }}>📄 Form CR</a>}
                  {result.pdf_bundle && <a href={result.pdf_bundle} target="_blank" rel="noreferrer" style={{ padding:"9px 16px", background:C.navy, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:12, fontWeight:700 }}>📦 Full Bundle</a>}
                  {result.pdf_slip   && <a href={result.pdf_slip}   target="_blank" rel="noreferrer" style={{ padding:"9px 16px", background:C.navy, color:"#fff", borderRadius:8, textDecoration:"none", fontSize:12, fontWeight:700 }}>🧾 Slip</a>}
                </div>
              </div>
            )}
          </Card>
        )}

      </div>
    </div>
  );
}